import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useApi, useWorkspace } from '~/contexts';
import { useAuth, useField, useIsMounted } from '~/hooks';
import { colors, weights } from '~/styles';
import AutoComplete from './AutoComplete';
import Dropdown from './Dropdown';
import FieldControl from './FieldControl';
import Icon from './Icon';

const Group = styled(Dropdown.Text)`
  font-weight: ${weights.bold};
  font-size: 1rem;
  color: ${colors.grey100};
`;

const CompanySelect = React.forwardRef(({ value, initialValue, allowNew, onChange, hasClient, ...props }, ref) => {
  const api = useApi();
  const isMounted = useIsMounted();
  const { workspace } = useWorkspace();
  const [defaultOptions, setDefaultOptions] = useState([]);
  const auth = useAuth();

  const handleSearch = useCallback(
    async (q) => {
      const { data } = await api.www
        .workspaces(workspace.id)
        .companies()
        .get({
          q,
          include: initialValue ? initialValue.id : undefined,
          size: 1000,
          hasClient,
          // permission, TODO: Implement permission in the route.
        });

      const groups = _.orderBy(
        data.reduce((a, v) => {
          const key = v.isActive ? 'Active' : 'Inactive';
          a[key] = a[key] || { key, companies: [] };
          a[key].companies.push(v);
          return a;
        }, {}),
        'key',
      );

      const options = [];
      for (const group of groups) {
        options.push({ type: 'group', group, disabled: true });
        group.companies.forEach((company) => {
          options.push({ type: 'company', company, disabled: false });
        });
      }
      return options;
    },
    [workspace.id, initialValue, hasClient, api],
  );

  const handleAddNew = async (value) => {
    const { data } = await api.www.workspaces(workspace.id).companies().upsert({ name: value });

    onChange({ target: { name: props.name, value: data } });
  };

  const refreshDefaultOptions = useCallback(async () => {
    const data = await handleSearch('');
    if (!isMounted.current) return;
    setDefaultOptions(data);
  }, [handleSearch, isMounted]);

  useEffect(() => {
    refreshDefaultOptions();
  }, [refreshDefaultOptions]);

  const handleChange = (event) => {
    const changeArgument = { target: { name: event.target.name, value: event.target.value?.company } };
    onChange(changeArgument);
  };

  return (
    <AutoComplete
      ref={ref}
      onSearch={handleSearch}
      value={value}
      displayText={value ? value.name : ''}
      defaultOptions={defaultOptions}
      onChange={handleChange}
      renderOption={(option) => option.company.name}
      {...props}>
      {({ options, inputValue, handleBlur, handleLoading }) => {
        const showAddNew = allowNew && auth.pipeline.manage && inputValue && inputValue !== value?.name;
        let index = 0;
        return (
          <>
            {showAddNew && (
              <>
                <AutoComplete.AddNewOption
                  disabled={!inputValue}
                  onClick={async () => {
                    handleLoading(true);
                    await handleAddNew(inputValue);
                    await refreshDefaultOptions();
                    handleBlur();
                    handleLoading(false);
                  }}>
                  <Icon icon="plus" size="xs" spaceRight />
                  Add {inputValue ? <strong>&nbsp;{inputValue}</strong> : ' New'}
                </AutoComplete.AddNewOption>
                {options.length > 0 && <AutoComplete.Separator />}
              </>
            )}
            {options.length > 0 ? (
              options.map((o) => {
                switch (o.type) {
                  case 'group':
                    return (
                      <Group key={o.group.key}>
                        <strong>{o.group.key}</strong>
                      </Group>
                    );
                  case 'company':
                    return (
                      <AutoComplete.Option
                        grey={!o.company.isActive === true ? 'true' : undefined}
                        padding="0.25rem 0 0.25rem 2rem"
                        key={o.company.id}
                        value={o}
                        index={index++}>
                        {o.company.name}
                      </AutoComplete.Option>
                    );
                  default:
                    return null;
                }
              })
            ) : (
              <Dropdown.Text>None</Dropdown.Text>
            )}
          </>
        );
      }}
    </AutoComplete>
  );
});

const FieldCompanySelect = React.forwardRef((props, ref) => {
  const [field, meta] = useField(props);
  const error = meta.touched && meta.error;

  return (
    <FieldControl error={error}>
      <CompanySelect ref={ref} {...field} {...props} />
    </FieldControl>
  );
});

export default CompanySelect;
export { FieldCompanySelect };
